/**
 * Filtratsiya dinamikasi — asosiy rejim. Holat {t, V, cakeThickness, dP}.
 *
 *   constantPressure: dV/dt = A²·ΔP/(μ·(α·c·V + R_m·A))  — RK4 (rk4Step), mahalliy-adaptiv
 *                     ichki qadam h = 0.2·(V + C)/Q (Q ning nisbiy o'zgarish vaqti), h ≥ dt/1000.
 *   constantRate:     ΔP(V) = Qμ(α·c·V + R_mA)/A² oshib boradi; V += Q·dt.
 *                     ΔP ≥ ΔP_max bo'lgan V_lim dan keyin — o'zgarmas bosim ΔP_max (yuqoridagi ODE).
 *                     Qadam ichida V_lim kesib o'tilsa, qadam ikkiga bo'linadi (aniq o'tish).
 *   Cho'kma to'lsa (L ≥ L_max): Q = 0, V o'zgarmaydi, `filtration.cakeFull` (critical).
 *   ΔP = 0 (yoki Q = 0 berilgan): `filtration.noDrivingForce`, Q = 0.
 *
 * Boshlang'ich holat: V = 0, toza to'siq; ΔP — berilgan yoki Q uchun zarur bo'lgan (V = 0).
 *
 * MANBA: Kasatkin (filtrlash tenglamalari); Perry's 18-bob.
 */
import { rk4Step } from '../../solvers';
import { warn, type StepResult, type Warning } from '../../types';
import {
  DEFAULT_TEMPERATURE,
  cakeThickness,
  flowAt,
  pressureForRate,
  ruthConstants,
  specificResistance,
  validateFiltrationInputs,
  validateFiltrationParams,
  viscosityOf,
  volumeForThickness,
} from './ruth';
import type {
  FiltrationInputs,
  FiltrationOutputs,
  FiltrationParams,
  FiltrationState,
} from './types';

const MAX_SUBSTEPS = 1000;
const SAFETY = 0.2;

export function initState(p: FiltrationParams, inputs: FiltrationInputs): FiltrationState {
  validateFiltrationParams(p);
  validateFiltrationInputs(p, inputs);
  const mu = viscosityOf(p, inputs.temperature ?? DEFAULT_TEMPERATURE);
  const dP =
    p.mode === 'constantPressure'
      ? (inputs.pressureDrop as number)
      : Math.min(
          p.maxPressure as number,
          pressureForRate(p, mu, inputs.solidsConcentration, 0, inputs.flowRate as number),
        );
  return { t: 0, V: 0, cakeThickness: 0, dP };
}

/** O'zgarmas ΔP da V ni dt davomida integratsiya; V_full ga yetsa to'xtaydi. */
function integrateConstantPressure(
  p: FiltrationParams,
  mu: number,
  alpha: number,
  c: number,
  dP: number,
  V0: number,
  dt: number,
  Vfull: number,
): number {
  const { K, C } = ruthConstants(p, mu, alpha, c, dP);
  const deriv = (_t: number, y: readonly number[]) => [flowAt(p, mu, alpha, c, y[0] as number, dP)];
  const hMin = dt / MAX_SUBSTEPS;
  let y = [V0];
  let remaining = dt;
  let count = 0;
  while (remaining > 1e-12 * dt && count < MAX_SUBSTEPS) {
    const V = y[0] as number;
    const Q = flowAt(p, mu, alpha, c, V, dP);
    if (!Number.isFinite(Q)) {
      // R_m = 0, V = 0: ODE singulyar (Q → ∞), lekin V(t) = √(K·t) chekli —
      // birinchi ichki qadam analitik (Ruth), so'ng RK4 davom etadi.
      const h = Math.min(remaining, hMin);
      y = [-C + Math.sqrt((V + C) * (V + C) + K * h)];
      remaining -= h;
      count++;
      continue;
    }
    const tau = Number.isFinite(C) && Q > 0 ? (V + C) / Q : Infinity;
    const h = Math.min(remaining, Math.max(hMin, SAFETY * tau));
    y = rk4Step(deriv, 0, y, h);
    remaining -= h;
    count++;
    if ((y[0] as number) >= Vfull) return Vfull;
  }
  return y[0] as number;
}

export function stepDynamic(
  p: FiltrationParams,
  state: FiltrationState,
  inputs: FiltrationInputs,
  dt: number,
): StepResult<FiltrationState, FiltrationOutputs> {
  validateFiltrationInputs(p, inputs);
  if (!(dt > 0)) throw new Error('filtration: dt must be > 0');
  const warnings: Warning[] = [];
  const T = inputs.temperature ?? DEFAULT_TEMPERATURE;
  const mu = viscosityOf(p, T, warnings);
  const c = inputs.solidsConcentration;
  const Vfull = volumeForThickness(p, c, p.maxCakeThickness);
  const t1 = state.t + dt;

  const noResistance = p.mediumResistance <= 0 && c <= 0;
  if (noResistance) warn(warnings, 'filtration.noResistance', 'critical');

  let V = state.V;
  let dP: number;
  let pressureLimited = false;

  const full0 = V >= Vfull * (1 - 1e-12);
  if (p.mode === 'constantPressure') {
    dP = inputs.pressureDrop as number;
    if (dP <= 0) warn(warnings, 'filtration.noDrivingForce', 'warning');
    else if (!full0 && !noResistance) {
      V = integrateConstantPressure(p, mu, specificResistance(p, dP), c, dP, V, dt, Vfull);
    }
  } else {
    const Q = inputs.flowRate as number;
    const Pmax = p.maxPressure as number;
    if (Q <= 0) {
      dP = 0;
      warn(warnings, 'filtration.noDrivingForce', 'warning');
    } else if (full0 || noResistance) {
      dP = Math.min(Pmax, pressureForRate(p, mu, c, V, Q));
    } else {
      // Qaysi V da ΔP_max ga yetiladi (α ΔP_max da)
      const aMax = specificResistance(p, Pmax);
      const A = p.area;
      const Vlim =
        c > 0 && aMax > 0
          ? ((Pmax * A * A) / (Q * mu) - p.mediumResistance * A) / (aMax * c)
          : pressureForRate(p, mu, c, V, Q) >= Pmax
            ? V
            : Infinity;
      const Vstop = Math.min(Vfull, Math.max(V, Vlim));
      const tRate = Math.min(dt, (Vstop - V) / Q);
      V += Q * tRate;
      const rest = dt - tRate;
      if (V >= Vlim - 1e-15) {
        pressureLimited = true;
        warn(warnings, 'filtration.pressureLimit', 'warning', { dPmax: Pmax });
        dP = Pmax;
        if (rest > 0 && V < Vfull) {
          V = integrateConstantPressure(p, mu, aMax, c, Pmax, V, rest, Vfull);
        }
      } else {
        dP = pressureForRate(p, mu, c, V, Q);
      }
    }
  }

  const cakeFull = V >= Vfull * (1 - 1e-12);
  if (cakeFull) {
    V = Math.min(V, Vfull);
    warn(warnings, 'filtration.cakeFull', 'critical');
  }
  const alpha = specificResistance(p, dP);
  const Qnow =
    cakeFull || noResistance ? 0 : Math.min(flowAt(p, mu, alpha, c, V, dP), Number.MAX_VALUE);
  const Qout =
    p.mode === 'constantRate' && !pressureLimited && !cakeFull && (inputs.flowRate as number) > 0
      ? (inputs.flowRate as number)
      : Qnow;
  const L = cakeThickness(p, c, V);
  const { K, C } = ruthConstants(p, mu, alpha, c, dP);
  const next: FiltrationState = { t: t1, V, cakeThickness: L, dP };
  return {
    state: next,
    outputs: {
      t: t1,
      V,
      Q: Qout,
      cakeThickness: L,
      dP,
      K,
      C,
      averageRate: t1 > 0 ? V / t1 : 0,
      viscosity: mu,
      specificCakeResistance: alpha,
      cakeFull,
      pressureLimited,
    },
    warnings,
  };
}
